// src/pages/SalesInvoices/SalesInvoices.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, onSnapshot, query, orderBy, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable'; // Changed import to load the plugin properly
import { font } from '../Orders/Roboto-Regular-font';

// --- STYLES ---
const styles = {
    container: { fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    box: { background: 'white', padding: '25px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { background: '#f8f9fa', padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' },
    td: { padding: '12px', borderBottom: '1px solid #eee' },
    trHover: { cursor: 'pointer' },
    searchInput: { padding: '10px 15px', fontSize: '16px', border: '1px solid #dee2e6', borderRadius: '5px', width: '300px' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { background: 'white', padding: '30px', borderRadius: '8px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #dee2e6', paddingBottom: '15px', marginBottom: '20px' },
    button: { padding: '10px 20px', border: 'none', background: '#06C167', color: 'white', borderRadius: '5px', cursor: 'pointer', fontSize: '16px' }
};

// --- YARDIMCI FONKSİYONLAR ---
const safeString = (value, fallback = '-') => (value === null || typeof value === 'undefined' || value === '') ? fallback : String(value).trim();
const safeNumber = (value, fallback = 0) => (typeof value !== 'number' || isNaN(value)) ? fallback : value;

// --- PDF OLUŞTURMA ---
const generateInvoicePDF = (companyInfo, invoice, products, canViewPrices = true) => {
    try {
        const getProductName = (productId) => products.find(p => p.id === productId)?.name || 'Bilinmeyen Ürün';
        const getProductBarcode = (productId) => products.find(p => p.id === productId)?.barcode || '-';
        const doc = new jsPDF();
        
        doc.addFileToVFS('Roboto-Regular.ttf', font);
        doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
        doc.setFont('Roboto');

        doc.setFontSize(22);
        doc.setTextColor(44, 62, 80);
        doc.text(safeString(companyInfo?.companyName, "Firma Adı"), 14, 22);
        doc.setFontSize(18);
        doc.setTextColor(149, 165, 166);
        doc.text("FATURA", 200, 22, { align: 'right' });
        doc.line(14, 28, 200, 28);

        doc.setFontSize(10);
        doc.text("Firma Bilgileri", 14, 40);
        doc.setFontSize(9);
        doc.text(safeString(companyInfo?.address), 14, 46);
        doc.text(`Tel: ${safeString(companyInfo?.phone)}`, 14, 52);
        doc.text(`VD: ${safeString(companyInfo?.taxOffice)} / ${safeString(companyInfo?.taxNumber)}`, 14, 58);

        doc.setFontSize(10);
        doc.text("Müşteri Bilgileri", 120, 40);
        doc.setFontSize(9);
        doc.text(safeString(invoice.customerName), 120, 46);

        doc.setFontSize(10);
        doc.text("Fatura No:", 200, 40, { align: 'right' });
        doc.setFontSize(9);
        doc.text(invoice.id.substring(0, 8).toUpperCase(), 200, 46, { align: 'right' });
        doc.setFontSize(10);
        doc.text("Tarih:", 200, 52, { align: 'right' });
        doc.setFontSize(9);
        const invoiceDate = invoice.invoiceDate?.seconds ? new Date(invoice.invoiceDate.seconds * 1000).toLocaleDateString() : 'Tarih Yok';
        doc.text(invoiceDate, 200, 58, { align: 'right' });

        const tableColumn = ["Stok Kodu", "Ürün Adı", "Miktar", "Birim Fiyat", "Toplam Tutar"];
        const tableRows = invoice.lineItems.map(item => [
            getProductBarcode(item.productId),
            getProductName(item.productId),
            safeString(item.quantity),
            canViewPrices ? `${safeNumber(item.price).toFixed(2)} ₺` : '***',
            canViewPrices ? `${safeNumber(item.price * item.quantity).toFixed(2)} ₺` : '***',
        ]);

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 75,
            theme: 'striped',
            headStyles: { fillColor: [44, 62, 80] },
            styles: { font: 'Roboto', fontStyle: 'normal' },
        });

        const finalY = doc.lastAutoTable.finalY;
        doc.setFontSize(10);
        doc.text(`Ara Toplam:`, 160, finalY + 15, { align: 'right' });
        doc.text(canViewPrices ? `${safeNumber(invoice.subTotal).toFixed(2)} ₺` : '***', 200, finalY + 15, { align: 'right' });
        doc.text(`KDV Tutarı:`, 160, finalY + 22, { align: 'right' });
        doc.text(canViewPrices ? `${safeNumber(invoice.vatAmount).toFixed(2)} ₺` : '***', 200, finalY + 22, { align: 'right' });
        doc.setFontSize(12);
        doc.setFont('Roboto', 'bold');
        doc.text(`Genel Toplam:`, 160, finalY + 30, { align: 'right' });
        doc.text(canViewPrices ? `${safeNumber(invoice.total).toFixed(2)} ₺` : '***', 200, finalY + 30, { align: 'right' });

        doc.save(`fatura-${invoice.id.substring(0, 6)}.pdf`);
    } catch (error) {
        console.error("Fatura PDF'i oluşturulurken hata:", error);
        alert("PDF oluşturulurken bir hata oluştu.");
    }
};

// --- FATURA DETAY MODALI ---
function InvoiceDetailModal({ invoice, products, companyInfo, onClose, canViewPrices }) {
    const getProductName = (productId) => products.find(p => p.id === productId)?.name || 'Bilinmeyen Ürün';
    const getProductBarcode = (productId) => products.find(p => p.id === productId)?.barcode || '-';

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '100%',
            maxWidth: '900px',
            height: '100vh',
            background: 'white',
            boxShadow: '-10px 0 25px rgba(0,0,0,0.1)',
            zIndex: 10002,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '"Inter", sans-serif'
        }} onClick={e => e.stopPropagation()}>
            <div style={{
                padding: '30px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f8fafc'
            }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#1e293b' }}>Fatura Detayı</h2>
                    <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>#{invoice.id.substring(0, 8).toUpperCase()}</p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button 
                        onClick={() => generateInvoicePDF(companyInfo, invoice, products, canViewPrices)} 
                        style={{
                            ...styles.button,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: '#10b981'
                        }} 
                        disabled={!companyInfo}
                    >
                        <span>🖨️</span> PDF İndir
                    </button>
                    <button 
                        onClick={onClose} 
                        style={{
                            background: '#f1f5f9',
                            border: 'none',
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '20px',
                            color: '#64748b'
                        }}
                    >
                        &times;
                    </button>
                </div>
            </div>

            <div style={{ padding: '30px', flex: 1, overflowY: 'auto', background: '#fff' }}>
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '24px', 
                    marginBottom: '32px',
                    background: '#f8fafc',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0'
                }}>
                    <div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Müşteri Bilgileri</div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{invoice.customerName}</div>
                        <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>{invoice.customerPhone || 'Telefon Yok'}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>Fatura Bilgileri</div>
                        <div style={{ fontSize: '14px', color: '#1e293b' }}><strong>Tarih:</strong> {new Date(invoice.invoiceDate.seconds * 1000).toLocaleDateString('tr-TR')}</div>
                        <div style={{ fontSize: '14px', color: '#1e293b', marginTop: '4px' }}><strong>Toplam:</strong> <span style={{ fontWeight: '700', color: '#06C167' }}>{canViewPrices ? `${invoice.total.toFixed(2)} ₺` : '***'}</span></div>
                    </div>
                </div>

                <div className="responsive-table-wrapper" style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                    <table style={{ ...styles.table, marginBottom: 0 }}>
                        <thead style={{ background: '#f8fafc' }}>
                            <tr>
                                <th style={{ ...styles.th, borderBottom: '1px solid #e2e8f0' }}>Stok Kodu</th>
                                <th style={{ ...styles.th, borderBottom: '1px solid #e2e8f0' }}>Ürün</th>
                                <th style={{ ...styles.th, borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>Miktar</th>
                                <th style={{ ...styles.th, borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>Fiyat</th>
                                <th style={{ ...styles.th, borderBottom: '1px solid #e2e8f0', textAlign: 'right' }}>Toplam</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoice.lineItems.map((item, index) => (
                                <tr key={index}>
                                    <td style={styles.td}>{getProductBarcode(item.productId)}</td>
                                    <td style={{ ...styles.td, fontWeight: '600' }}>{getProductName(item.productId)}</td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>{item.quantity}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>{canViewPrices ? `${item.price.toFixed(2)} ₺` : '***'}</td>
                                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700' }}>{canViewPrices ? `${(item.price * item.quantity).toFixed(2)} ₺` : '***'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ width: '300px', background: '#f8fafc', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#64748b' }}>
                            <span>Ara Toplam</span>
                            <span>{canViewPrices ? `${(invoice.subTotal || 0).toFixed(2)} ₺` : '***'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#64748b' }}>
                            <span>KDV Tutarı</span>
                            <span>{canViewPrices ? `${(invoice.vatAmount || 0).toFixed(2)} ₺` : '***'}</span>
                        </div>
                        <div style={{ height: '1px', background: '#e2e8f0', margin: '12px 0' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>
                            <span>Genel Toplam</span>
                            <span>{canViewPrices ? `${invoice.total.toFixed(2)} ₺` : '***'}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SalesInvoices() {
    const { userData, canViewPrices } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [products, setProducts] = useState([]);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }
        const companyId = userData.companyId;

        const fetchCompanyInfo = async () => {
            const docRef = doc(db, 'companies', companyId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) setCompanyInfo(docSnap.data());
        };
        fetchCompanyInfo();

        const q = query(collection(db, 'companies', companyId, 'invoices'), orderBy('invoiceDate', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        const qPayments = query(collection(db, 'companies', companyId, 'transactions'), where('type', '==', 'Tahsilat'));
        const unsubPayments = onSnapshot(qPayments, (snapshot) => {
            setPayments(snapshot.docs.map(doc => doc.data()));
        });

        const unsubProducts = onSnapshot(collection(db, 'companies', companyId, 'products'), snap => {
            setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => { unsubscribe(); unsubPayments(); unsubProducts(); };
    }, [userData]);

    const filteredInvoices = useMemo(() => {
        const paidMap = {};
        payments.forEach(p => {
            const targetId = p.invoiceId || p.orderId;
            if (targetId) {
                paidMap[targetId] = (paidMap[targetId] || 0) + (p.amount || 0);
            }
        });

        return invoices.filter(inv => 
            inv.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inv.id.toLowerCase().includes(searchTerm.toLowerCase())
        ).map(inv => {
            const paidAmount = paidMap[inv.id] || (inv.orderId ? paidMap[inv.orderId] : 0) || 0;
            const totalAmount = inv.total || 0;
            return {
                ...inv,
                paidAmount,
                paymentPercentage: totalAmount > 0 ? Math.min(100, (paidAmount / totalAmount) * 100) : 0
            };
        });
    }, [invoices, searchTerm, payments]);

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1>Satış Faturaları</h1>
                <input 
                    type="text"
                    placeholder="Müşteri veya Fatura No Ara..."
                    style={styles.searchInput}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            <div style={styles.box}>
                {loading ? <p>Yükleniyor...</p> : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Fatura No</th>
                                <th style={styles.th}>Müşteri</th>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Tutar</th>
                                <th style={styles.th}>Ödeme Durumu</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInvoices.map(invoice => (
                                <tr key={invoice.id} style={styles.trHover} onClick={() => setSelectedInvoice(invoice)}>
                                    <td style={styles.td}>{invoice.id.substring(0, 8).toUpperCase()}</td>
                                    <td style={styles.td}>{invoice.customerName}</td>
                                    <td style={styles.td}>{new Date(invoice.invoiceDate.seconds * 1000).toLocaleDateString()}</td>
                                    <td style={styles.td}>{canViewPrices ? `${invoice.total.toFixed(2)} ₺` : '***'}</td>
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '100px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: '600', color: (invoice.paymentPercentage || 0) >= 100 ? '#10b981' : '#06C167' }}>
                                                %{(invoice.paymentPercentage || 0).toFixed(0)}
                                            </div>
                                            <div style={{ width: '100%', height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                                <div style={{ width: `${invoice.paymentPercentage || 0}%`, height: '100%', background: (invoice.paymentPercentage || 0) >= 100 ? '#10b981' : '#06C167', transition: 'width 0.3s ease' }} />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <AnimatePresence>
                {selectedInvoice && (
                    <>
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedInvoice(null)}
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0,0,0,0.3)',
                                backdropFilter: 'blur(4px)',
                                zIndex: 10001
                            }}
                        />
                        <motion.div
                            initial={{ x: '100%', opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: '100%', opacity: 0 }}
                            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                            style={{
                                position: 'fixed',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                width: '100%',
                                maxWidth: '900px',
                                zIndex: 10002
                            }}
                        >
                            <InvoiceDetailModal 
                                invoice={selectedInvoice} 
                                products={products}
                                companyInfo={companyInfo}
                                onClose={() => setSelectedInvoice(null)} 
                                canViewPrices={canViewPrices}
                            />
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}

export default SalesInvoices;